import { useEffect, useState } from "react";

import {
  msUntilNextDayKey,
  todayKey,
  type DateKey,
  type DayBoundaryOptions,
} from "./dates";

/**
 * The current day key, kept correct while the app is left open.
 *
 * Kobo is a menu-bar app people never quit: without this, a window opened on
 * Tuesday still says "Today" on Wednesday morning and writes new tasks into
 * Tuesday's bucket. Every screen that means "today" must read it from here
 * rather than calling `todayKey()` during render, so all of them roll at the
 * same instant.
 *
 * Two independent triggers, because either one alone leaks:
 *
 * - a timer armed for the exact next boundary — covers a window left open
 *   overnight. It is re-armed from the real clock after each fire rather than
 *   repeating every 24h, so DST and long sleeps cannot make it drift.
 * - a visibility/focus check — a laptop asleep at midnight does not fire
 *   timers, so the day is also re-checked the moment the app comes back.
 */
export function useDayKey(options: DayBoundaryOptions = {}): DateKey {
  const { dayStartHour } = options;
  const [key, setKey] = useState(() => todayKey(new Date(), { dayStartHour }));

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const sync = () => {
      setKey((current) => {
        const next = todayKey(new Date(), { dayStartHour });
        return next === current ? current : next;
      });
    };

    const arm = () => {
      clearTimeout(timer);
      // A small cushion past the boundary so a fractionally early wake-up does
      // not read the previous day and immediately re-arm for a few milliseconds.
      timer = setTimeout(() => {
        sync();
        arm();
      }, msUntilNextDayKey(new Date(), { dayStartHour }) + 250);
    };

    const onWake = () => {
      sync();
      arm();
    };

    arm();
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [dayStartHour]);

  return key;
}
